import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, MoveUpRight } from "lucide-react";
import { pricingTiers } from "@/components/landing/data";

const pricingSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Pricing({ openBooking }) {
  const [openTier, setOpenTier] = useState(null);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 sm:py-32 lg:py-40">
      <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent" aria-hidden="true" />
      <div className="section-shell">
        <div className="mb-16 max-w-4xl">
          <p data-testid="pricing-eyebrow" className="eyebrow">Pricing / direct</p>
          <h2 data-testid="pricing-heading" className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-[-0.05em] text-white sm:text-6xl">Clear offers. No mystery retainers.</h2>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {pricingTiers.map((tier, index) => (
            <motion.article
              key={tier.name}
              data-testid={`pricing-card-${pricingSlug(tier.name)}`}
              initial={{ opacity: 0, y: 42 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -10 }}
              className={`relative flex flex-col rounded-[1.8rem] border p-8 ${tier.name === "Nonprofit Rate" ? "lg:col-start-3 " : ""}${tier.popular ? "border-cyan-300/50 bg-cyan-300/[0.08] shadow-[0_0_80px_rgba(0,240,255,0.13)] lg:-mt-8" : "border-white/10 bg-[#0F0F17]/85"}`}
            >
              {tier.popular && <span data-testid="pricing-popular-badge" className="absolute -top-4 left-8 rounded-full bg-[#CCFF00] px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black">Most selected</span>}
              <h3 className="font-display text-2xl font-bold uppercase text-white">{tier.name}</h3>
              <div className="mt-8 flex items-end gap-3"><span className="font-display text-6xl font-extrabold tracking-[-0.06em] text-white">{tier.price}</span><span className="pb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{tier.billing}</span></div>
              <p className="mt-6 min-h-28 text-sm leading-relaxed text-slate-400">{tier.description}</p>
              <div className="mt-8 border-t border-white/10 pt-6">
                <button
                  type="button"
                  data-testid={`pricing-details-${pricingSlug(tier.name)}-button`}
                  aria-expanded={openTier === tier.name}
                  onClick={() => setOpenTier(openTier === tier.name ? null : tier.name)}
                  className="flex w-full items-center justify-between rounded-full border border-white/10 bg-white/[0.03] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-[border-color,color,background-color] duration-300 hover:border-cyan-300/40 hover:bg-cyan-300/10 hover:text-cyan-200"
                >
                  Details
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${openTier === tier.name ? "rotate-180 text-cyan-300" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openTier === tier.name && (
                    <motion.ul
                      data-testid={`pricing-details-${pricingSlug(tier.name)}-list`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="mt-5 space-y-4 overflow-hidden"
                    >
                      {tier.features.map((feature) => <li key={feature} className="flex items-center gap-3 text-sm text-slate-300"><Check className="h-4 w-4 text-cyan-300" />{feature}</li>)}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
              <button
                data-testid={`pricing-select-${pricingSlug(tier.name)}-button`}
                onClick={() => openBooking(tier.name)}
                className={`${tier.popular ? "cyan-button" : "outline-button"} mt-10 rounded-full px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em]`}
              >
                {tier.cta} <MoveUpRight className="h-4 w-4" />
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
